import { apiClient } from './client'
import type { CursorPage } from './accounts'
import type { BackgroundJobDetailView, BackgroundJobView, ErrorReportView } from '../types/job'

/** The four `JobKind` wire values (lower-snake — matches `JobKind.fromWire`, the REQUEST-side
 * casing; see `types/job.ts`'s wire-format note for why this differs from the RESPONSE's `kind`
 * casing) relevant to the Data screen's "Recent activity" filter. Every other `JobKind` (campaign
 * sends, exports, dossier assembly, …) is real too, just out of this screen's declared scope. */
export const DATA_JOB_KINDS = ['ingestion_run', 'bulk_update', 'bulk_delete', 'bulk_purge'] as const
export type DataJobKindFilter = (typeof DATA_JOB_KINDS)[number] | 'all'

export interface ListJobsParams {
  /** Lower-snake `JobKind` wire value, or omitted/undefined for every kind. */
  kind?: string | null
  /** Lower-snake `JobStatus` wire value (`queued|running|completed|completed_with_errors|failed|
   * cancelled`), or omitted/undefined for every status. */
  status?: string | null
  cursor?: string | null
  limit?: number
}

/** Internal-only — {@link listMyJobs} is the sole caller that ever sets `initiatedBy`, always to
 * `"me"`, so no other caller of {@link listBackgroundJobs} can accidentally widen the query to a
 * tenant-wide read (IB-27 S2 design §4.1). */
interface InternalListParams extends ListJobsParams {
  initiatedBy?: string | null
}

function toQuery(params: InternalListParams): string {
  const q = new URLSearchParams()
  if (params.kind) q.set('kind', params.kind)
  if (params.status) q.set('status', params.status)
  if (params.initiatedBy) q.set('initiated_by', params.initiatedBy)
  if (params.cursor) q.set('cursor', params.cursor)
  q.set('limit', String(params.limit ?? 50))
  return q.toString()
}

/** `GET /background-jobs` (already-published `BackgroundJobController`) — the Data screen's
 * "Recent activity": one unified read across ingestion runs, bulk update/delete/purge, and every
 * other async `JobKind` this platform submits. */
export async function listBackgroundJobs(
  params: ListJobsParams,
  signal?: AbortSignal,
): Promise<CursorPage<BackgroundJobView>> {
  const { data } = await apiClient.get<CursorPage<BackgroundJobView>>(`/background-jobs?${toQuery(params)}`, {
    signal,
  })
  return data
}

/** `GET /background-jobs/{id}` — a row's expanded detail (deep link + full counts). */
export async function getBackgroundJob(id: string, signal?: AbortSignal): Promise<BackgroundJobDetailView> {
  const { data } = await apiClient.get<BackgroundJobDetailView>(`/background-jobs/${id}`, { signal })
  return data
}

/** `GET /background-jobs?initiated_by=me` — the personal Jobs tray's ONLY list call (IB-27 S2
 * design §2.1/§4.1, FR-M23.11): `initiated_by=me` is a real server-side filter
 * (`BackgroundJobController.resolveInitiatedBy`) resolving against the ambient caller, already
 * covered by `BackgroundJobTenancyIsolationIntegrationTest` — this wrapper is the ONE place that
 * sets it, so no tray caller can forget it / accidentally widen the read to the whole tenant. */
export async function listMyJobs(
  params: ListJobsParams = {},
  signal?: AbortSignal,
): Promise<CursorPage<BackgroundJobView>> {
  const { data } = await apiClient.get<CursorPage<BackgroundJobView>>(
    `/background-jobs?${toQuery({ ...params, initiatedBy: 'me' })}`,
    { signal },
  )
  return data
}

/** `POST /background-jobs/{id}/cancellations` -> `202` with the UPDATED `BackgroundJobView` (IB-27
 * S2 design §2.3). Deliberately NOT optimistic: the caller applies the server's returned view (or
 * surfaces the server's actual error, e.g. `409 job_already_terminal`) rather than guessing a
 * client-side "cancelled" state before the response returns.
 *
 * No `Idempotency-Key`: `/api/v1/background-jobs/*` is not among `IdempotencyFilterConfig`'s
 * covered creating-POST prefixes, confirmed by reading `BackgroundJobCancelIntegrationTest`'s own
 * requests (they send none) rather than assuming either way (design §4.1/§6 RAID-5). */
export async function cancelBackgroundJob(id: string, signal?: AbortSignal): Promise<BackgroundJobView> {
  const { data } = await apiClient.post<BackgroundJobView>(`/background-jobs/${id}/cancellations`, undefined, {
    signal,
  })
  return data
}

/** `GET /background-jobs/{id}/error-report` -> a signed URL (IB-27 S2 design §2.4). `404`s when the
 * job has no `errorReportRef` — the caller surfaces that specific `ApiError`, never a generic
 * toast; the tray only ever calls this when the list row's `errorReportRef != null` in the first
 * place, so a 404 here means the report expired/was revoked between render and click, not a UI bug. */
export async function getJobErrorReportUrl(id: string, signal?: AbortSignal): Promise<ErrorReportView> {
  const { data } = await apiClient.get<ErrorReportView>(`/background-jobs/${id}/error-report`, { signal })
  return data
}
